import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  deadOps,
  discardAllDead,
  discardDeadOp,
  retryAllDead,
  retryDeadOp,
  useSyncStatus,
  type OutboxOp,
} from '@dia/db';
import { useT } from '@dia/i18n';
import { Button } from '@dia/ui';
import { sync } from '../supabase';
import { Page, Notice } from './lib/ui';
import { CheckingWho, useIdentity } from './lib/identity';

/**
 * The parking bay (defect D5).
 *
 * Before this, one write the island would never accept sat at the head of the
 * queue and held every tick behind it, behind a badge that only ever said
 * "1 sin enviar". Now the drain parks it here after six tries, carries on, and
 * puts a red badge on every screen with a link to this one.
 *
 * Two choices, and both are the person's, never the app's: try it again (the
 * island may have been fixed since), or throw it away. Throwing away is the
 * only place in this app where a write is lost on purpose, so it asks twice.
 */

function describe(op: OutboxOp, lang: 'es' | 'en'): string {
  const when = new Date(op.createdAt).toLocaleString(lang === 'es' ? 'es-PA' : 'en-US', {
    timeZone: 'America/Panama',
    dateStyle: 'short',
    timeStyle: 'short',
  });
  const what = op.kind === 'update' ? (lang === 'es' ? 'cambio' : 'change') : lang === 'es' ? 'nuevo' : 'new';
  return `${op.table} (${what}), ${when}`;
}

export function SinEnviar() {
  const { t, lang } = useT();
  const { identity, ready } = useIdentity();
  const status = useSyncStatus(sync);
  const [rows, setRows] = useState<OutboxOp[]>([]);
  const [confirming, setConfirming] = useState<string | null>(null);

  const reload = useCallback(async () => setRows(await deadOps()), []);

  useEffect(() => {
    void reload();
    // The count in the badge changes whenever a drain runs, and the list under
    // it has to follow.
  }, [reload, status.dead]);

  if (!identity) return ready ? <Navigate to="/entrar" replace /> : <CheckingWho />;

  const after = async () => {
    setConfirming(null);
    await reload();
    await sync.tick('manual');
  };

  return (
    <Page title={t('sync.dead_title')} back="/">
      {rows.length === 0 ? (
        <Notice tone="ok">{t('sync.dead_none')}</Notice>
      ) : (
        <>
          <Notice tone="danger">{t('sync.dead_explain')}</Notice>

          <ul className="mt-4 flex flex-col gap-3">
            {rows.map((op) => (
              <li key={op.id} className="rounded border border-line bg-surface p-3">
                <p className="font-medium">{describe(op, lang)}</p>
                {op.lastError && <p className="mt-1 text-inkMuted break-words">{op.lastError}</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    onClick={async () => {
                      await retryDeadOp(op.id);
                      await after();
                    }}
                  >
                    {t('sync.dead_retry')}
                  </Button>
                  {confirming === op.id ? (
                    <Button
                      variant="danger"
                      onClick={async () => {
                        await discardDeadOp(op.id);
                        await after();
                      }}
                    >
                      {t('sync.dead_confirm')}
                    </Button>
                  ) : (
                    <Button variant="quiet" onClick={() => setConfirming(op.id)}>
                      {t('sync.dead_discard')}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap gap-2">
            <Button
              onClick={async () => {
                await retryAllDead();
                await after();
              }}
            >
              {t('sync.dead_retry_all')}
            </Button>
            {confirming === 'all' ? (
              <Button
                variant="danger"
                onClick={async () => {
                  await discardAllDead();
                  await after();
                }}
              >
                {t('sync.dead_confirm')}
              </Button>
            ) : (
              <Button variant="quiet" onClick={() => setConfirming('all')}>
                {t('sync.dead_discard_all')}
              </Button>
            )}
          </div>
        </>
      )}
    </Page>
  );
}
